import { useEffect, useMemo, useState } from 'react';

import {
  AlertCircleIcon,
  CheckmarkBadge02Icon,
  Download01Icon,
  SentIcon,
  ThumbsDownIcon,
  ViewIcon,
} from '@hugeicons/core-free-icons';
import { Upload01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import {
  DATE_FORMAT,
  DISPUTE_FORM_URL,
  PDF_MIME_TYPE,
  VARIANT_BG_CLASSES,
  VARIANT_TEXT_CLASSES,
} from 'constants/candidate-portal';
import { saveAs } from 'file-saver';
import {
  findCheckById,
  findCheckVariant,
  formatCheckStatus,
  formatDateForDisplay,
  formatDateSafely,
  generateReportFileName,
  getTextFieldSafely,
  processFileResponse as processFileResponseHelper,
  splitDateSafely,
} from 'helpers/candidate-portal-helpers';
import { now } from 'helpers/date';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { usePortalUploadActions } from 'hooks/upload-actions';
import { PDFDocument } from 'pdf-lib';
import { useTranslation } from 'react-i18next';

import Alert from 'components/Alert/Alert';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { FilePreview } from 'components/FilePreview/FilePreview';
import { Button } from 'components/FormComponents';
import { DocumentUpload } from 'components/FormComponents/DocumentUpload/DocumentUpload';
import { IconComponent } from 'components/Icon/IconComponent';
import SliderMotion from 'components/Motion/SliderMotion';
import { StatusPill } from 'components/Pills/StatusPills';

import {
  useLazyGetCheckResultFileQuery,
  useLazyGetSubCheckResultFileQuery,
  useSubmitCheckDisputeMutation,
  useUpdateCandidateCheckMutation,
} from 'api/candidate-portal-api';

const HIDE_REPORT_CHECK_TYPES = ['custom_reference', 'reference'];

export const CandidateCheckslist = ({
  candidate,
  company,
  checks,
  brand = null,
  updateChecks = () => {},
}) => {
  const { t } = useTranslation();
  const [triggerGetCheckResultFile] = useLazyGetCheckResultFileQuery();
  const [triggerGetSubCheckResultFile] = useLazyGetSubCheckResultFileQuery();
  const [updateCandidateCheck] = useUpdateCandidateCheckMutation();
  const [submitCheckDispute] = useSubmitCheckDisputeMutation();

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [selectedCheckId, setSelectedCheckId] = useState(null);
  const [selectedSubCheckId, setSelectedSubCheckId] = useState(null);
  const [showDisputeFormModal, setShowDisputeFormModal] = useState(false);
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [showReferralReleaseConsentModal, setShowReferralReleaseConsentModal] = useState(false);

  const { getUpload, signUpload, createUpload, deleteUpload } = usePortalUploadActions();

  /**
   * Processes file response and sets preview state.
   * Uses data URLs or HTTPS URLs only (no blob:), so CSP does not need blob:.
   */
  const processFileResponse = async (result, fileId) => {
    const fileObject = await processFileResponseHelper(result, fileId);
    setPreviewFile(fileObject);
    setShowPreviewModal(true);
  };

  const handleViewSubCheckReport = async subCheckId => {
    if (!subCheckId) {
      console.error('Sub check ID is required');
      return;
    }

    try {
      setSelectedSubCheckId(subCheckId);
      setSelectedCheckId(null); // Clear check ID when viewing sub-check to prevent stale ID in filename
      const result = await triggerGetSubCheckResultFile({ subCheckId }).unwrap();
      await processFileResponse(result, subCheckId);
    } catch (error) {
      console.error('Error fetching sub check result file:', error);
      // TODO: Show user-friendly error message
    }
  };

  const handleViewReport = async checkId => {
    if (!checkId) {
      console.error('Check ID is required');
      return;
    }

    try {
      setSelectedCheckId(checkId);
      setSelectedSubCheckId(null); // Clear sub-check ID when viewing check to prevent stale ID in filename
      const result = await triggerGetCheckResultFile({ candidateCheckId: checkId }).unwrap();
      await processFileResponse(result, checkId);
    } catch (error) {
      console.error('Error fetching check result file:', error);
      // TODO: Show user-friendly error message
    }
  };

  const checkDetails = useMemo(() => {
    return findCheckById(checks, selectedCheckId);
  }, [selectedCheckId, checks]);

  const subCheckDetails = useMemo(() => {
    if (!selectedSubCheckId || !checks || !Array.isArray(checks)) {
      return null;
    }
    // Find the sub-check by searching through all checks and their sub_checks
    for (const check of checks) {
      if (check?.sub_checks && Array.isArray(check.sub_checks)) {
        const subCheck = check.sub_checks.find(sc => sc?.id === selectedSubCheckId);
        if (subCheck) {
          return subCheck;
        }
      }
    }
    return null;
  }, [selectedSubCheckId, checks]);

  const updateExtraDataField = async (extraDataField, value) => {
    const { data: result } = await updateCandidateCheck({
      candidateCheckId: checkDetails?.id,
      params: { extra_data: { ...checkDetails?.extra_data, [extraDataField]: value } },
    });
    updateChecks(curr => curr.map(check => (check.id === result.id ? result : check)));
  };

  const downloadDisputeForm = async () => {
    if (!checkDetails) {
      console.error('Check details are required to download dispute form');
      return;
    }

    try {
      // Fetch PDF form
      const response = await fetch(DISPUTE_FORM_URL);
      if (!response.ok) {
        throw new Error(`Failed to fetch dispute form: ${response.status} ${response.statusText}`);
      }

      const formPdfBytes = await response.arrayBuffer();
      if (!formPdfBytes || formPdfBytes.byteLength === 0) {
        throw new Error('Dispute form PDF is empty');
      }

      // Load PDF document
      const pdfDoc = await PDFDocument.load(formPdfBytes);
      const form = pdfDoc.getForm();

      // Get all fields and filter for text fields (for debugging)
      const allFields = form.getFields();
      const textFields = allFields
        .filter(field => {
          // Check if field is a text field by checking its constructor name
          return field?.constructor?.name === 'PDFTextField';
        })
        .map(field => ({
          name: field.getName(),
          value: field.getText() || '',
          isReadOnly: field.isReadOnly(),
        }));

      if (textFields.length > 0) {
        console.info(`Found ${textFields.length} text field(s)`);
      } else {
        console.warn('No text fields found in the PDF form');
      }

      // Set organisation reference number
      const orgRefField = getTextFieldSafely(form, 'Organisation reference number');
      if (orgRefField) {
        const orgRef =
          checkDetails?.extra_data?.organisation_reference_number ||
          `CM-${checkDetails?.id || 'unknown'}`;
        orgRefField.setText(orgRef);
      }

      // Set accredited body
      const accreditedBodyField = getTextFieldSafely(form, 'Accredited body');
      if (accreditedBodyField && checkDetails?.extra_data?.accredited_body) {
        accreditedBodyField.setText(checkDetails.extra_data.accredited_body);
      }

      // Submit date from candidate_check.job_at; release date from result upload.created_at
      const nssSubmitDate = formatDateSafely(checkDetails?.job_at);
      const nssReleaseDate = formatDateSafely(checkDetails?.result_upload_created_at);

      // Split dates and set date fields
      if (nssSubmitDate) {
        const [day, month, year] = splitDateSafely(nssSubmitDate);

        const dateField1 = getTextFieldSafely(form, 'Submit date 1 day');
        if (dateField1) dateField1.setText(day);

        const dateField2 = getTextFieldSafely(form, 'Submit date 1 month');
        if (dateField2) dateField2.setText(month);

        const dateField3 = getTextFieldSafely(form, 'Submit date 1 year');
        if (dateField3) dateField3.setText(year);
      }

      if (nssReleaseDate) {
        const [day, month, year] = splitDateSafely(nssReleaseDate);

        const releaseDateField1 = getTextFieldSafely(form, 'Submit date day');
        if (releaseDateField1) releaseDateField1.setText(day);

        const releaseDateField2 = getTextFieldSafely(form, 'Submit date month');
        if (releaseDateField2) releaseDateField2.setText(month);

        const releaseDateField3 = getTextFieldSafely(form, 'Submit date year');
        if (releaseDateField3) releaseDateField3.setText(year);
      }

      // Save PDF
      const pdfBytes = await pdfDoc.save();

      // Create a blob and trigger download
      const blob = new Blob([pdfBytes], { type: PDF_MIME_TYPE });
      saveAs(blob, 'acic_dispute_form_filled.pdf');

      updateExtraDataField('dispute_status', 'pending_form');

      setShowDisputeFormModal(false);
      handleClosePreview();
    } catch (error) {
      console.error('Error downloading dispute form:', error);
      // TODO: Show user-friendly error message
      alert('Failed to download dispute form. Please try again later.');
    }
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    // Revoke blob URL only if something still provided one (we use data URLs / HTTPS now)
    if (previewFile?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(previewFile.url);
    }
    setPreviewFile(null);
  };

  const handleDownloadReport = async () => {
    if (!previewFile?.url) {
      console.error('No file to download');
      return;
    }

    try {
      const response = await fetch(previewFile.url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      const fileName = generateReportFileName({
        checkName: selectedSubCheckId ? subCheckDetails?.title : checkDetails?.title,
        checkId: selectedCheckId,
        subCheckId: selectedSubCheckId,
      });
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Download failed:', error);
      // TODO: Show user-friendly error message
    }
  };

  const submitDispute = async () => {
    await submitCheckDispute({ candidateCheckId: checkDetails?.id });
    await updateExtraDataField('dispute_status', 'dispute_submitted');
    setShowDocumentUploadModal(false);
  };

  const showReportForCheck = useMemo(
    () => check => !HIDE_REPORT_CHECK_TYPES.includes(check?.type),
    [],
  );

  const releaseToClient = async (referral_consented = false) => {
    if (checkDetails?.details?.referral_candidate && !referral_consented) {
      setShowReferralReleaseConsentModal(true);
      return;
    }

    let params = { completed_at: now() };
    if (referral_consented) {
      params.extra_data = {
        ...checkDetails?.extra_data,
        referral_consent: referral_consented,
      };
    }

    const { data: result } = await updateCandidateCheck({
      candidateCheckId: checkDetails?.id,
      params,
    });
    updateChecks(curr => curr.map(check => (check.id === result.id ? result : check)));
    setShowPreviewModal(false);
    setShowReferralReleaseConsentModal(false);
  };

  // Cleanup blob URL on unmount if one was ever used (we use data/HTTPS URLs now)
  useEffect(() => {
    return () => {
      if (previewFile?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(previewFile.url);
      }
    };
  }, [previewFile]);

  // Icon mapping for check variants
  const variantIcons = {
    disputed: ThumbsDownIcon,
    releasedToClient: SentIcon,
    actionRequired: AlertCircleIcon,
    ready: CheckmarkBadge02Icon,
  };

  return (
    <>
      <AnimatedModal
        title="Upload Dispute Form"
        description="Upload the dispute form to the portal. This will be used to dispute the report."
        visible={showDocumentUploadModal}
        small={true}
        icon={Upload01Icon}
        oldIconLibrary={false}
        onClose={() => setShowDocumentUploadModal(false)}>
        <DocumentUpload
          modelParams={{
            candidate_id: candidate?.id,
            company_id: company?.id,
          }}
          metadata={{
            check_id: checkDetails?.id,
            dispute_form: true,
          }}
          accept={{
            'application/pdf': ['.pdf'],
          }}
          documentTitle="Dispute Form"
          getUpload={getUpload}
          signUpload={signUpload}
          createUpload={createUpload}
          deleteUpload={deleteUpload}
          allowDisableFormActions={false}
          brand={brand}
        />
        <Button onClick={submitDispute} variant="secondary" size="lg" className="w-full mt-10">
          Submit
        </Button>
      </AnimatedModal>

      <Alert
        show={showDisputeFormModal}
        title="Dispute Report"
        message={`Disputing this report we will review the report and get it back to you shortly. In order for dispute to proceed, you must first download the dispute form using the download dispute form button and upload it back to the portal under checks in your dashboard.`}
        ok="Download Dispute Form"
        onOk={() => {
          downloadDisputeForm();
        }}
        cancel="Go Back"
        icon={ThumbsDownIcon}
        type="error"
        onCancel={() => setShowDisputeFormModal(false)}
      />
      <AnimatedModal
        visible={showPreviewModal}
        onClose={handleClosePreview}
        title="Report"
        fullscreen={true}
        noPadding={true}
        noClose={false}>
        {previewFile && (
          <div className="flex flex-col h-full max-h-[calc(100vh-20px)]">
            <AnimatedModal
              visible={showReferralReleaseConsentModal}
              onClose={() => setShowReferralReleaseConsentModal(false)}
              title="Referral Candidate"
              icon={AlertCircleIcon}
              oldIconLibrary={false}
              small>
              <p>
                I acknowledge that I was referred by {company.name} for the purposes of obtaining a
                background check and that Checkmate Technology Pty Ltd have completed that check on
                my behalf. I have reviewed the background check outcome, and am happy with the
                results and understand that Checkmate Technology will automatically delete the check
                result 90 days after it was completed.
              </p>
              <p className="mt-4">
                I understand that I am under no obligation to share the check result with{' '}
                {company?.name}, however I may choose to do so, and that by clicking 'Share Results'
                I consent to my Nationally Coordinated Criminal History Check results being shared
                with {company?.name}.
              </p>
              <div className="flex justify-end gap-5 mt-4">
                <Button
                  type="secondary-reverse"
                  onClick={() => setShowReferralReleaseConsentModal(false)}>
                  Cancel
                </Button>
                <Button type="primary" onClick={() => releaseToClient(true)}>
                  Share Results
                </Button>
              </div>
            </AnimatedModal>
            <div className="flex-1 overflow-y-auto overflow-x-hidden px-8 pb-24">
              <FilePreview
                file={previewFile}
                displayMetadata={false}
                zoom={true}
                enableZoom={false}
              />
            </div>
            <div
              className="flex items-center gap-4 justify-end pt-4 border-t border-border-primary sticky bottom-0 bg-surface-primary z-10 px-8 py-4"
              role="toolbar"
              aria-label={t('candidatePortal.reportActions')}>
              <Button onClick={handleDownloadReport} icon="download" type="secondary" small>
                {t('candidatePortal.downloadReport')}
              </Button>
              {checkDetails?.type === 'acic_check' &&
                !checkDetails?.completed_at &&
                (checkDetails?.result_flag || checkDetails?.details?.referral_candidate) &&
                checkDetails?.extra_data?.dispute_status !== 'dispute_submitted' && (
                  <>
                    <Button
                      onClick={() => setShowDisputeFormModal(true)}
                      icon="thumbs-down"
                      type="warn"
                      small>
                      Dispute
                    </Button>
                    <Button onClick={() => releaseToClient()} icon="send" small type="success">
                      Release to client
                    </Button>
                  </>
                )}
            </div>
          </div>
        )}
      </AnimatedModal>
      <SliderMotion key={`footer-${candidate?.id || 'default'}`} duration={0.2}>
        <section
          className="flex flex-col bg-surface-primary rounded-3xl shadow-sm border border-border-primary shadow-soft"
          aria-labelledby="checks-heading">
          <div className="flex flex-col gap-2 p-8">
            <h2 id="checks-heading" className="text-2xl font-semibold text-text-primary m-0">
              {t('candidatePortal.backgroundChecks')}
            </h2>
          </div>
          <hr className="border-border-primary" aria-hidden="true" />
          {!checks || checks.length === 0 ? (
            <div className="flex p-8" role="status">
              <p className="text-xl text-text-secondary font-regular m-0">
                {t('candidatePortal.noBackgroundChecksFound')}
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-4 mt-4 list-none p-0 m-0">
              {checks.map(inmutableCheck => {
                let check = { ...inmutableCheck };
                if (!check?.id) {
                  return null;
                }

                // ACIC statuses
                if (check.type === 'acic_check') {
                  if (check.completed_at) {
                    check.releasedToClient = true;
                  } else if (check.extra_data?.dispute_status === 'dispute_submitted') {
                    check.disputed = true;
                  } else if (check.upload_id) {
                    check.actionRequired = true;
                  }
                }

                const variantConfig = findCheckVariant(check, variantIcons);
                return (
                  <li
                    key={check.id}
                    className="flex flex-col gap-4 pb-4 border-b border-border-primary last:border-b-0">
                    <div className="flex items-center justify-between gap-4 px-8">
                      <div className="flex items-center gap-6 w-full flex-1">
                        <IconComponent
                          icon={getCheckTypeIcon(check.type)}
                          className={
                            brand?.color_button && !check.canceled
                              ? `!bg-[var(--brand-badge-bg)] !text-[var(--brand-text)] !border-0`
                              : check.canceled
                                ? '!border-0 opacity-50'
                                : ''
                          }
                          type={brand?.color_button ? 'secondary' : ''}
                          aria-hidden="true"
                        />
                        <div className="flex flex-col gap-2 w-full">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-2xl font-regular text-text-primary m-0">
                              {check.title}
                            </h3>
                          </div>
                          <time
                            className={`text-xl text-text-tertiary font-regular ${
                              check.completed_at
                                ? 'text-green-3'
                                : check.submitted_at
                                  ? 'text-blue-3'
                                  : 'text-yellow-3'
                            }`}
                            dateTime={check.completed_at || check.submitted_at || check.created_at}>
                            {check.completed_at
                              ? `${t('candidatePortal.completed')}: ${formatDateForDisplay(check.completed_at)}`
                              : check.submitted_at
                                ? `${t('candidatePortal.submitted')}: ${formatDateForDisplay(check.submitted_at)}`
                                : `${t('candidatePortal.created')}: ${formatDateForDisplay(check.created_at)}`}
                          </time>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span
                          aria-label={t('candidatePortal.checkStatus', {
                            status: formatCheckStatus(check.status),
                          })}>
                          <StatusPill
                            status={check.status}
                            text={formatCheckStatus(check.status)}
                            small
                            className="px-4"
                          />
                        </span>
                      </div>
                    </div>
                    {check?.sub_checks &&
                      Array.isArray(check.sub_checks) &&
                      check.sub_checks.length > 0 && (
                        <div
                          className="flex flex-col mt-4 ml-32 mr-8 bg-neutral-2 rounded-3xl border border-border-primary"
                          role="list"
                          aria-label={t('candidatePortal.subChecks')}>
                          {check.sub_checks.map(subCheck => {
                            if (!subCheck?.id) {
                              return null;
                            }

                            return (
                              <div
                                key={subCheck.id}
                                className="flex items-center justify-between border-b border-border-primary last:border-b-0 p-4"
                                role="listitem">
                                <div className="flex items-center gap-6 w-full flex-1">
                                  <div className="flex flex-col gap-2 w-full">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-xl font-regular text-text-primary m-0">
                                        {subCheck.title || ''}
                                      </h4>
                                    </div>
                                    <time
                                      className={`text-lg text-text-tertiary font-regular ${
                                        subCheck.completed_at
                                          ? 'text-green-3'
                                          : subCheck.submitted_at
                                            ? 'text-blue-3'
                                            : 'text-yellow-3'
                                      }`}
                                      dateTime={
                                        subCheck.completed_at ||
                                        subCheck.submitted_at ||
                                        subCheck.created_at
                                      }>
                                      {subCheck.completed_at
                                        ? `${t('candidatePortal.completed')}: ${formatDateForDisplay(subCheck.completed_at)}`
                                        : subCheck.submitted_at
                                          ? `${t('candidatePortal.submitted')}: ${formatDateForDisplay(subCheck.submitted_at)}`
                                          : `${t('candidatePortal.created')}: ${formatDateForDisplay(subCheck.created_at)}`}
                                    </time>
                                  </div>
                                </div>
                                {subCheck.upload_id && showReportForCheck(check) && (
                                  <div className="flex items-center gap-4">
                                    <button
                                      type="button"
                                      onClick={() => handleViewSubCheckReport(subCheck.id)}
                                      aria-label={t('candidatePortal.viewReportForSubCheck', {
                                        subCheck: subCheck.title || '',
                                      })}
                                      className={cn(
                                        'w-full sm:w-max justify-center text-xl text-text-primary cursor-pointer font-regular bg-surface-primary border border-border-primary rounded-full px-8 py-4 flex items-center gap-2 transition-colors duration-200 no-underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary',
                                        brand?.color_button
                                          ? 'hover:bg-[var(--brand-button-bg)] hover:text-[var(--brand-button-text)] hover:border-[var(--brand-button-bg)]'
                                          : 'hover:bg-primary-4',
                                      )}>
                                      <HugeiconsIcon
                                        icon={ViewIcon}
                                        size="16"
                                        strokeWidth={1.5}
                                        aria-hidden="true"
                                      />
                                      {t('candidatePortal.viewReport')}
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    {check?.upload_id && showReportForCheck(check) && (
                      <div className="flex flex-col gap-4 px-8">
                        <div
                          className={cn(
                            'flex flex-col gap-4 p-8 rounded-3xl',
                            VARIANT_BG_CLASSES[variantConfig.variant],
                          )}>
                          <div className="flex flex-col gap-2">
                            <span
                              className={cn(
                                'text-xl font-semibold flex items-center gap-2',
                                VARIANT_TEXT_CLASSES[variantConfig.variant],
                              )}>
                              <HugeiconsIcon
                                icon={variantConfig.icon}
                                size="20"
                                strokeWidth={1.5}
                                aria-hidden="true"
                              />{' '}
                              {variantConfig.title}
                            </span>
                            <span className="text-xl text-text-secondary font-regular">
                              {variantConfig.message}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 sm:flex-row flex-col w-full sm:w-auto">
                          <button
                            type="button"
                            onClick={() => handleViewReport(check.id)}
                            aria-label={t('candidatePortal.viewReportForCheck', {
                              check: check.title,
                            })}
                            className={cn(
                              'w-full sm:w-max justify-center text-xl text-text-primary cursor-pointer font-regular bg-surface-secondary border border-border-primary rounded-full px-8 py-4 flex items-center gap-2 transition-colors duration-200 no-underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary',
                              brand?.color_button
                                ? 'hover:bg-[var(--brand-button-bg)] hover:text-[var(--brand-button-text)] hover:border-[var(--brand-button-bg)]'
                                : 'hover:bg-primary-4',
                            )}>
                            <HugeiconsIcon
                              icon={ViewIcon}
                              size="16"
                              strokeWidth={1.5}
                              aria-hidden="true"
                            />
                            {t('candidatePortal.viewReport')}
                          </button>
                          {check.extra_data?.dispute_status && !check.completed_at && (
                            <Button
                              onClick={() => {
                                setSelectedCheckId(check.id);
                                setShowDocumentUploadModal(true);
                              }}
                              small
                              icon={
                                check.extra_data?.dispute_status === 'pending_form'
                                  ? 'upload'
                                  : 'loader'
                              }
                              disabled={check.extra_data?.dispute_status === 'dispute_submitted'}
                              variant="secondary"
                              size="lg">
                              {check.extra_data?.dispute_status === 'pending_form'
                                ? 'Upload Dispute Form'
                                : 'Dispute in progress'}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </SliderMotion>
    </>
  );
};
